import { domainTools } from "@lib/domain";
import { httpTools } from "@lib/redux/http-utils";
import { apiSlice } from "@features/application/api-slice";
import { createBulkReadMutation } from "@features/feed/bulk-read";
import routes from "@features/routes";
import { ProgramProposalEntity, ProgramProposalOutputView } from "./types/program-proposal";
import { programProposalsTools } from "@features/events-program/program-proposals-tools";
import { BaseEntitiesProvidesId, NestedEntitiesProvidesId } from "@features/application/types";
import {
  ProposalAssignmentAggregateEntity,
  ProposalAssignmentAggregateOutputView,
} from "@features/events-program/types/proposal-assignment-aggregate";
import {
  ProposalAssignmentCandidateEntity,
  ProposalAssignmentCandidateOutputView,
  ProposalAssignmentCloseInputView,
  ProposalAssignmentEntity,
  ProposalAssignmentInputView,
  ProposalAssignmentOutputView,
  ProposalAssignmentReorderInputView,
  ProposalAssignmentReplaceInputView,
} from "@features/events-program/types/proposal-assignment";
import { StreamingResponseBody } from "@/types/network";
import {
  ProposalAttachmentEntity,
  ProposalAttachmentLinkInputView,
  ProposalAttachmentOutputView,
  ProposalAttachmentUpdatingInputView,
} from "@features/events-program/types/proposal-attachment";
import {
  ProposalVoteCreatingInputView,
  ProposalVoteEntity,
  ProposalVoteOutputView,
  ProposalVoteUpdatingInputView,
} from "@features/events-program/types/proposal-vote";
import {
  ProposalCommentBaseOutputView,
  ProposalCommentReplyEntity,
  ProposalCommentReplyInputView,
  ProposalCommentReplyOutputView,
  ProposalCommentRootEntity,
  ProposalCommentRootInputView,
  ProposalCommentRootOutputView,
  ProposalCommentThreadEntity,
  ProposalCommentThreadOutputView,
  ProposalCommentUpdatingInputView,
} from "@features/events-program/types/proposal-comment";

const programProposalsExtraEntitiesCacheExtractor: NestedEntitiesProvidesId<ProgramProposalOutputView>[] =
  [
    {
      type: "Activity",
      extractor: (proposal) => proposal?.activity?.id,
    },
    {
      type: "ActivityMember",
      extractor: (proposal) => proposal?.members?.map((m) => m.id),
    },
    {
      type: "ProposalAssignment",
      extractor: (proposal) => proposal?.assignments?.map((a) => a.id),
    },
    {
      type: "ProposalAttachment",
      extractor: (proposal) => proposal?.attachments?.map((a) => a.id),
    },
    {
      type: "ProposalVote",
      extractor: (proposal) => proposal?.votes?.map((v) => v.id),
    },
  ];

const baseProposalsExtraEntitiesCacheExtractor: BaseEntitiesProvidesId[] = [
  {
    type: "ProposalAssignment",
    extractor: "LIST",
  },
  {
    type: "ProposalAttachment",
    extractor: "LIST",
  },
  {
    type: "ProposalVote",
    extractor: "LIST",
  },
  {
    type: "ProposalCommentRoot",
    extractor: "LIST",
  },
  {
    type: "ProposalCommentReply",
    extractor: "LIST",
  },
  {
    type: "ProposalComment",
    extractor: "LIST",
  },
];

const assignmentAggregatesExtraEntitiesCacheExtractor: NestedEntitiesProvidesId<ProposalAssignmentAggregateEntity>[] =
  [
    {
      type: "ProposalAssignment",
      extractor: (aggregate) => aggregate?.assignment?.id,
    },
    {
      type: "ProgramProposal",
      extractor: (aggregate) => aggregate?.proposal?.id,
    },
    {
      type: "ProposalVote",
      extractor: (aggregate) => aggregate?.proposal?.vote?.id,
    },
    {
      type: "Activity",
      extractor: (aggregate) => aggregate?.proposal?.activity?.id,
    },
    {
      type: "ActivityMember",
      extractor: (aggregate) => aggregate?.proposal?.members?.map((m) => m.id),
    },
    {
      type: "Event",
      extractor: (aggregate) => aggregate?.event?.id,
    },
  ];

const assignmentAggregatesRootEntitiesCacheExtractor: BaseEntitiesProvidesId[] = [
  {
    type: "ProposalVote",
    extractor: "LIST",
  },
  {
    type: "ProposalAssignment",
    extractor: "LIST",
  },
];

export const programApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProgramProposals: httpTools.getMultipleResponse<
      ProgramProposalOutputView,
      ProgramProposalEntity
    >(
      builder,
      routes.events_program.api.program_proposals,
      "ProgramProposal",
      (data) =>
        data?.map(programProposalsTools.convertToDomainEntity).sort(domainTools.sortByIdDesc),
      programProposalsExtraEntitiesCacheExtractor,
      null,
      baseProposalsExtraEntitiesCacheExtractor,
    ),
    bulkReadProgramProposals: createBulkReadMutation<ProgramProposalOutputView>(
      builder,
      routes.events_program.api.program_proposals_bulk_read,
    ),
    getProgramProposal: httpTools.getOnceResponse<ProgramProposalOutputView, ProgramProposalEntity>(
      builder,
      routes.events_program.api.program_proposal,
      "ProgramProposal",
      (data) => (data ? programProposalsTools.convertToDomainEntity(data) : undefined),
      programProposalsExtraEntitiesCacheExtractor,
    ),
    getProgramAssignmentAggregates: httpTools.getMultipleResponse<
      ProposalAssignmentAggregateOutputView,
      ProposalAssignmentAggregateEntity
    >(
      builder,
      routes.events_program.api.program_assignments,
      null,
      (data) => data?.map(programProposalsTools.convertAssignmentAggregateToDomainEntity),
      assignmentAggregatesExtraEntitiesCacheExtractor,
      null,
      assignmentAggregatesRootEntitiesCacheExtractor,
    ),
    bulkReadAssignments: createBulkReadMutation<ProposalAssignmentAggregateOutputView>(
      builder,
      routes.events_program.api.program_assignments_bulk_read,
    ),
    bulkReadProposalAssignments: createBulkReadMutation<ProposalAssignmentOutputView>(
      builder,
      routes.events_program.api.proposal_assignments_bulk_read,
    ),

    putProposalAssignments: httpTools.putMultipleResponse<
      ProposalAssignmentOutputView,
      ProposalAssignmentEntity,
      ProposalAssignmentInputView
    >(
      builder,
      routes.events_program.api.proposal_assignments,
      "ProposalAssignment",
      (data) =>
        data
          ?.map(programProposalsTools.convertAssignmentToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      "none",
    ),
    closeProposalAssignments: httpTools.postMultipleResponse<
      ProposalAssignmentOutputView,
      ProposalAssignmentEntity,
      ProposalAssignmentCloseInputView
    >(
      builder,
      routes.events_program.api.proposal_assignments_close,
      "ProposalAssignment",
      (data) =>
        data
          ?.map(programProposalsTools.convertAssignmentToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      "none",
    ),
    replaceProposalAssignments: httpTools.postMultipleResponse<
      ProposalAssignmentOutputView,
      ProposalAssignmentEntity,
      ProposalAssignmentReplaceInputView
    >(
      builder,
      routes.events_program.api.proposal_assignments_replace,
      "ProposalAssignment",
      (data) =>
        data
          ?.map(programProposalsTools.convertAssignmentToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      "none",
    ),
    reopenProposalAssignments: httpTools.postMultipleResponse<
      ProposalAssignmentOutputView,
      ProposalAssignmentEntity,
      ProposalAssignmentInputView
    >(
      builder,
      routes.events_program.api.proposal_assignments_reopen,
      "ProposalAssignment",
      (data) =>
        data
          ?.map(programProposalsTools.convertAssignmentToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      "list",
      [
        {
          type: "ProgramProposal",
          extractor: () => "LIST",
        },
        {
          type: "ProgramProposal",
          extractor: (assignment) => assignment?.proposalId,
        },
      ],
    ),
    reconcileProposalAssignments: httpTools.postMultipleResponse<
      ProposalAssignmentOutputView,
      ProposalAssignmentEntity,
      ProposalAssignmentInputView
    >(
      builder,
      routes.events_program.api.proposal_assignments_reconcile,
      "ProposalAssignment",
      (data) =>
        data
          ?.map(programProposalsTools.convertAssignmentToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      "list",
      [
        {
          type: "ProgramProposal",
          extractor: () => "LIST",
        },
        {
          type: "ProgramProposal",
          extractor: (assignment) => assignment?.proposalId,
        },
      ],
    ),
    reorderProposalAssignments: httpTools.putMultipleResponse<
      ProposalAssignmentOutputView,
      ProposalAssignmentEntity,
      ProposalAssignmentReorderInputView
    >(
      builder,
      routes.events_program.api.proposal_assignments_reorder,
      "ProposalAssignment",
      (data) =>
        data
          ?.map(programProposalsTools.convertAssignmentToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      "none",
    ),

    getProposalsAttachment: httpTools.getOnceResponse<
      ProposalAttachmentOutputView,
      ProposalAttachmentEntity
    >(builder, routes.events_program.api.proposal_attachment, "ProposalAttachment", (data) =>
      data ? programProposalsTools.convertAttachmentToDomainEntity(data) : undefined,
    ),
    getProposalsAttachments: httpTools.getMultipleResponse<
      ProposalAttachmentOutputView,
      ProposalAttachmentEntity
    >(builder, routes.events_program.api.proposal_attachments, "ProposalAttachment", (data) =>
      data
        ?.map(programProposalsTools.convertAttachmentToDomainEntity)
        .sort(domainTools.sortByIdDesc),
    ),
    bulkReadProposalAttachments: createBulkReadMutation<ProposalAttachmentOutputView>(
      builder,
      routes.events_program.api.proposal_attachments_bulk_read,
    ),
    postProposalAttachmentLink: httpTools.postOnceResponse<
      ProposalAttachmentOutputView,
      ProposalAttachmentEntity,
      ProposalAttachmentLinkInputView
    >(
      builder,
      routes.events_program.api.proposal_attachment_links,
      "ProposalAttachment",
      (data) => (data ? programProposalsTools.convertAttachmentToDomainEntity(data) : undefined),
      "list",
    ),
    postProposalAttachmentFile: httpTools.postOnceResponse<
      ProposalAttachmentOutputView,
      ProposalAttachmentEntity,
      FormData
    >(
      builder,
      routes.events_program.api.proposal_attachment_files,
      "ProposalAttachment",
      (data) => (data ? programProposalsTools.convertAttachmentToDomainEntity(data) : undefined),
      "list",
    ),
    deleteProposalAttachment: httpTools.deleteOnceResponse<
      ProposalAttachmentOutputView,
      ProposalAttachmentEntity
    >(builder, routes.events_program.api.proposal_attachment, "ProposalAttachment", (data) =>
      data ? programProposalsTools.convertAttachmentToDomainEntity(data) : undefined,
    ),
    patchProposalAttachment: httpTools.patchOnceResponse<
      ProposalAttachmentOutputView,
      ProposalAttachmentEntity,
      ProposalAttachmentUpdatingInputView
    >(builder, routes.events_program.api.proposal_attachment, "ProposalAttachment", (data) =>
      data ? programProposalsTools.convertAttachmentToDomainEntity(data) : undefined,
    ),
    getProposalAttachmentContent: httpTools.getOnceResponse<
      StreamingResponseBody,
      StreamingResponseBody
    >(builder, routes.events_program.api.proposal_attachment_content, null),

    getAssignmentCandidates: httpTools.getMultipleResponse<
      ProposalAssignmentCandidateOutputView,
      ProposalAssignmentCandidateEntity
    >(
      builder,
      routes.events_program.api.assignment_candidates,
      null,
      (data) =>
        data
          ?.map(programProposalsTools.convertAssignmentCandidateToDomainEntity)
          .sort(domainTools.sortByIdDesc),
      [
        {
          type: "TeamMember",
          extractor: (candidate) => candidate?.teamMember?.id,
        },
        {
          type: "ProposalAssignment",
          extractor: () => "LIST",
        },
      ],
    ),

    getProposalVotes: httpTools.getMultipleResponse<ProposalVoteOutputView, ProposalVoteEntity>(
      builder,
      routes.events_program.api.proposal_votes,
      "ProposalVote",
      (data) =>
        data?.map(programProposalsTools.convertVoteToDomainEntity).sort(domainTools.sortByIdDesc),
    ),
    bulkReadProposalVotes: createBulkReadMutation<ProposalVoteOutputView>(
      builder,
      routes.events_program.api.proposal_votes_bulk_read,
    ),
    getProposalVote: httpTools.getOnceResponse<ProposalVoteOutputView, ProposalVoteEntity>(
      builder,
      routes.events_program.api.proposal_vote,
      "ProposalVote",
      (data) => (data ? programProposalsTools.convertVoteToDomainEntity(data) : undefined),
    ),
    postProposalVote: httpTools.postOnceResponse<
      ProposalVoteOutputView,
      ProposalVoteEntity,
      ProposalVoteCreatingInputView
    >(
      builder,
      routes.events_program.api.proposal_votes,
      "ProposalVote",
      (data) => (data ? programProposalsTools.convertVoteToDomainEntity(data) : undefined),
      "list",
    ),
    patchProposalVote: httpTools.patchOnceResponse<
      ProposalVoteOutputView,
      ProposalVoteEntity,
      ProposalVoteUpdatingInputView
    >(builder, routes.events_program.api.proposal_vote, "ProposalVote", (data) =>
      data ? programProposalsTools.convertVoteToDomainEntity(data) : undefined,
    ),
    deleteProposalVote: httpTools.deleteOnceResponse<ProposalVoteOutputView, ProposalVoteEntity>(
      builder,
      routes.events_program.api.proposal_vote,
      "ProposalVote",
      (data) => (data ? programProposalsTools.convertVoteToDomainEntity(data) : undefined),
    ),

    getProposalComments: httpTools.getMultipleResponse<
      ProposalCommentThreadOutputView,
      ProposalCommentThreadEntity
    >(
      builder,
      routes.events_program.api.proposal_comments,
      null,
      (data) => data?.map(programProposalsTools.convertCommentThreadToDomainEntity),
      [
        {
          type: "ProposalCommentRoot",
          extractor: (data) => data?.root?.id,
        },
        {
          type: "ProposalCommentReply",
          extractor: (data) => data?.replies?.map((r) => r.id) ?? [],
        },
      ],
      null,
      [
        {
          type: "ProposalCommentRoot",
          extractor: "LIST",
        },
        {
          type: "ProposalCommentReply",
          extractor: "LIST",
        },
        {
          type: "ProposalComment",
          extractor: "LIST",
        },
      ],
    ),
    bulkReadProposalComments: createBulkReadMutation<ProposalCommentBaseOutputView>(
      builder,
      routes.events_program.api.proposal_comments_bulk_read,
    ),
    postProposalRootComment: httpTools.postOnceResponse<
      ProposalCommentRootOutputView,
      ProposalCommentRootEntity,
      ProposalCommentRootInputView
    >(
      builder,
      routes.events_program.api.proposal_comments,
      "ProposalCommentRoot",
      (data) => (data ? programProposalsTools.convertCommentRootToDomainEntity(data) : undefined),
      "list",
    ),
    getProposalComment: httpTools.getOnceResponse<
      ProposalCommentBaseOutputView,
      ProposalCommentBaseOutputView
    >(builder, routes.events_program.api.proposal_comment, "ProposalComment"),
    patchProposalComment: httpTools.patchOnceResponse<
      ProposalCommentBaseOutputView,
      ProposalCommentBaseOutputView,
      ProposalCommentUpdatingInputView
    >(builder, routes.events_program.api.proposal_comment, "ProposalComment", null),
    deleteProposalComment: httpTools.deleteOnceResponse<
      ProposalCommentBaseOutputView,
      ProposalCommentBaseOutputView
    >(builder, routes.events_program.api.proposal_comment, "ProposalComment", null),
    postProposalCommentsReply: httpTools.postOnceResponse<
      ProposalCommentReplyOutputView,
      ProposalCommentReplyEntity,
      ProposalCommentReplyInputView
    >(
      builder,
      routes.events_program.api.proposal_comment_reply,
      "ProposalCommentReply",
      (data) => (data ? programProposalsTools.convertCommentReplyToDomainEntity(data) : undefined),
      "list",
    ),
    postProposalCommentsResolve: httpTools.postOnceResponse<
      ProposalCommentRootOutputView,
      ProposalCommentRootEntity,
      never
    >(
      builder,
      routes.events_program.api.proposal_comment_resolve,
      "ProposalCommentRoot",
      (data) => (data ? programProposalsTools.convertCommentRootToDomainEntity(data) : undefined),
      "list",
    ),
  }),
  overrideExisting: false,
});

export const {
  useGetProgramProposalsQuery,
  useBulkReadProgramProposalsMutation,
  useGetProgramProposalQuery,
  useGetProgramAssignmentAggregatesQuery,
  useBulkReadAssignmentsMutation,
  useBulkReadProposalAssignmentsMutation,
  usePostProposalAttachmentLinkMutation,
  usePostProposalAttachmentFileMutation,
  useDeleteProposalAttachmentMutation,
  usePatchProposalAttachmentMutation,
  useGetProposalAttachmentContentQuery,
  useLazyGetProposalAttachmentContentQuery,
  useBulkReadProposalAttachmentsMutation,
  useGetAssignmentCandidatesQuery,
  usePutProposalAssignmentsMutation,
  useCloseProposalAssignmentsMutation,
  useReplaceProposalAssignmentsMutation,
  useReopenProposalAssignmentsMutation,
  useReconcileProposalAssignmentsMutation,
  useReorderProposalAssignmentsMutation,
  useGetProposalVotesQuery,
  useBulkReadProposalVotesMutation,
  useGetProposalVoteQuery,
  useDeleteProposalVoteMutation,
  usePostProposalVoteMutation,
  usePatchProposalVoteMutation,
  useGetProposalCommentsQuery,
  useBulkReadProposalCommentsMutation,
  useGetProposalCommentQuery,
  usePatchProposalCommentMutation,
  usePostProposalCommentsReplyMutation,
  usePostProposalRootCommentMutation,
  usePostProposalCommentsResolveMutation,
} = programApi;
